import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { api, ErroApi } from './api.ts';

/** Mecânica compartilhada das telas de cadastro: lista, seleção, formulário,
 *  erros por campo e as três mutações. Só muda o que cada cadastro guarda. */
export function useCadastro<T extends { id: string | number }, F extends object>(opcoes: {
  recurso: string;
  vazio: F;
  /** Do registro da lista para os campos do formulário. */
  paraFormulario: (item: T) => F;
  /** Nome usado nas mensagens ("Cadastro de X criado"). */
  rotulo?: (item: T) => string;
  /** Querystring da listagem. */
  consulta?: string;
}) {
  const { recurso, vazio, paraFormulario, consulta = '' } = opcoes;
  const qc = useQueryClient();

  const [editando, setEditando] = useState<T['id'] | null>(null);
  const [form, setForm] = useState<F>(vazio);
  const [campos, setCampos] = useState<Record<string, string>>({});
  const [erroGeral, setErroGeral] = useState('');
  const [salvo, setSalvo] = useState('');

  const lista = useQuery({
    queryKey: [recurso, consulta],
    queryFn: ({ signal }) =>
      api.get<{ itens: T[]; total: number; iniciais?: string[] }>(`/${recurso}${consulta}`, signal),
    placeholderData: (anterior) => anterior,
  });

  const limpar = useCallback(() => {
    setEditando(null);
    setForm(vazio);
    setCampos({});
    setErroGeral('');
  }, [vazio]);

  const selecionar = useCallback(
    (item: T) => {
      setEditando(item.id);
      setForm(paraFormulario(item));
      setCampos({});
      setErroGeral('');
      setSalvo('');
    },
    [paraFormulario],
  );

  const aoFalhar = (e: unknown) => {
    if (e instanceof ErroApi) {
      setCampos(e.campos);
      if (!e.temCampos) setErroGeral(e.message);
    } else {
      setErroGeral('Não foi possível concluir.');
    }
  };

  const invalidar = () => {
    void qc.invalidateQueries({ queryKey: [recurso] });
    void qc.invalidateQueries({ queryKey: ['opcoes'] });
  };

  const salvar = useMutation({
    mutationFn: (dados: F) =>
      editando !== null
        ? api.put<T>(`/${recurso}/${editando}`, dados)
        : api.post<T>(`/${recurso}`, dados),
    onSuccess: (item) => {
      invalidar();
      const nome = opcoes.rotulo?.(item) ?? '';
      // "Cadastro de X criado" e não "X criada": o particípio concordaria em
      // gênero e nem todo cadastro tem como saber o do registro.
      setSalvo(`Cadastro${nome ? ` de ${nome}` : ''} ${editando !== null ? 'atualizado' : 'criado'}.`);
      limpar();
    },
    onError: aoFalhar,
  });

  const excluir = useMutation({
    mutationFn: (id: T['id']) => api.apagar(`/${recurso}/${id}`),
    onSuccess: () => {
      invalidar();
      setSalvo('Cadastro excluído.');
      limpar();
    },
    onError: (e) => setErroGeral(e instanceof ErroApi ? e.message : 'Não foi possível excluir.'),
  });

  /** Handler de campo controlado, com máscara opcional. */
  const mudar = useCallback(
    <C extends keyof F>(chave: C, mascara?: (v: string) => F[C]) =>
      (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const alvo = e.target;
        const bruto =
          alvo instanceof HTMLInputElement && alvo.type === 'checkbox'
            ? (alvo.checked as F[C])
            : (alvo.value as F[C]);
        setForm((f) => ({ ...f, [chave]: mascara ? mascara(String(alvo.value)) : bruto }));
      },
    [],
  );

  /** Altera um campo específico. Sempre funcional: `setForm({...form, x})`
   *  captura o estado do render anterior e, quando duas alterações acontecem
   *  antes de um novo render, a segunda desfaz a primeira. */
  const definir = useCallback(<C extends keyof F>(chave: C, valor: F[C]) => {
    setForm((f) => ({ ...f, [chave]: valor }));
  }, []);

  const enviar = (e: React.FormEvent) => {
    e.preventDefault();
    setCampos({});
    setErroGeral('');
    setSalvo('');
    salvar.mutate(form);
  };

  return {
    lista,
    itens: lista.data?.itens ?? [],
    total: lista.data?.total ?? 0,
    iniciais: lista.data?.iniciais ?? [],
    editando,
    form,
    setForm,
    definir,
    campos,
    erroGeral,
    setErroGeral,
    salvo,
    setSalvo,
    limpar,
    selecionar,
    mudar,
    enviar,
    salvando: salvar.isPending,
    excluir,
  };
}

/** Listas dos selects, buscadas de uma vez só. */
export interface Opcoes {
  especialidades: { id: number; nome: string }[];
  convenios: { id: number; nome: string }[];
  procedimentos: {
    id: number;
    nome: string;
    sigla: string | null;
    especialidadeId: number;
    valor: number | null;
    valorMedico: number | null;
    medicinaOcupacional: boolean;
    tipoLaudo: 'TEXTO' | 'OIT_2000';
  }[];
  empresas: { id: string; nome: string; cnpj: string }[];
  medicos: { id: string; nome: string; crm: string; especialidades: number[] }[];
  /** Todos os médicos ativos, laudem ou não: quem pede o exame costuma ser o
   *  clínico, e quem lauda é o radiologista. */
  solicitantes: { id: string; nome: string; crm: string }[];
}

export function useOpcoes() {
  return useQuery({
    queryKey: ['opcoes'],
    queryFn: ({ signal }) => api.get<Opcoes>('/opcoes', signal),
    staleTime: 5 * 60_000,
  });
}
